// src/utils/auth.ts
import { env } from '../config/env';

/**
 * Redirects user to Cognito logout endpoint
 * This is a standalone utility function that can be used anywhere without requiring hooks
 */
export const signOutRedirect = () => {
  try {
    // First, clear any local OIDC storage
    clearOidcStorage();
    
    // Use environment variables from env.ts
    const { clientId, logoutUri, cognitoDomain } = env.auth;
    
    console.log("Signing out with:", { clientId, logoutUri, cognitoDomain });
    
    // Validate required parameters
    if (!clientId) {
      console.error("Missing clientId for logout");
      throw new Error("Missing client configuration");
    }

    if (!cognitoDomain) {
      console.error("Missing cognitoDomain for logout");
      throw new Error("Missing domain configuration");
    }
    
    // Construct the logout URL with proper encoding - ENSURE client_id is used (not id_token_hint)
    const encodedLogoutUri = encodeURIComponent(logoutUri || window.location.origin + '/logout');
    const logoutUrl = `${cognitoDomain}/logout?client_id=${clientId}&logout_uri=${encodedLogoutUri}`;
    
    console.log("Redirecting to logout URL:", logoutUrl);
    
    // Redirect to Cognito logout endpoint with the right parameters
    window.location.href = logoutUrl;
    
    return true; // Return value to indicate success
  } catch (error) {
    console.error("Error in signOutRedirect:", error);
    // Fall back to direct navigation to logout page
    window.location.href = '/logout';
    return false; // Return value to indicate failure
  }
};

/**
 * Clear all OIDC-related storage items
 */
export const clearOidcStorage = () => {
  try {
    // Clear any localStorage items related to OIDC
    const storageKeys = Object.keys(localStorage);
    const oidcKeys = storageKeys.filter(key => 
      key.startsWith('oidc.') || 
      key.includes('oidc') || 
      key.includes('auth') || 
      key.includes('token')
    );
    
    oidcKeys.forEach(key => {
      console.log(`Clearing localStorage key: ${key}`);
      localStorage.removeItem(key);
    });
    
    // Also clear sessionStorage items
    const sessionKeys = Object.keys(sessionStorage);
    const oidcSessionKeys = sessionKeys.filter(key => 
      key.startsWith('oidc.') || 
      key.includes('oidc') || 
      key.includes('auth') || 
      key.includes('token')
    );
    
    oidcSessionKeys.forEach(key => {
      console.log(`Clearing sessionStorage key: ${key}`);
      sessionStorage.removeItem(key);
    });
    
    return true;
  } catch (e) {
    console.error("Error clearing storage:", e);
    return false;
  }
};

/**
 * Force a hard logout by clearing all auth tokens and redirecting
 * Use this as a last resort if normal signOut doesn't work
 */
export const forceLogout = () => {
  try {
    // Clear OIDC storage
    clearOidcStorage();
    
    // Redirect to logout page
    window.location.href = '/logout';
    
    return true;
  } catch (error) {
    console.error("Error in forceLogout:", error);
    
    // Last resort: direct navigation
    try {
      window.location.href = '/logout';
    } catch (e) {
      console.error("Failed final logout attempt:", e);
    }
    
    return false;
  }
};

/**
 * Check if a user profile has the Admin role
 * @param profile - The user profile object from the JWT token
 * @returns boolean indicating if the user has admin privileges
 */
export const hasAdminRole = (profile: any): boolean => {
  try {
    // Check for cognito:groups claim
    const groups = profile['cognito:groups'];
    
    if (Array.isArray(groups)) {
      return groups.includes('Admins');
    }
    
    // If no groups claim or not in expected format, check other potential admin indicators
    // This is a fallback mechanism
    if (typeof profile.roles === 'string') {
      return profile.roles.includes('admin') || profile.roles.includes('Admin');
    }
    
    if (Array.isArray(profile.roles)) {
      return profile.roles.includes('admin') || profile.roles.includes('Admin');
    }
    
    return false;
  } catch (error) {
    console.error('Error checking admin role:', error);
    return false;
  }
};